import { pipSourcesFromPlayback, type PipSources, type StudioPipPayload } from "@/lib/player-pip"
import {
  SCHEMA_VERSION,
  timelineDuration,
  type EngineSources,
  type TimelineDocument,
  type TimelineTrack,
} from "@/lib/timeline"

export type StudioPlaybackKind = "video" | "previz" | "animatic" | "still" | "black"

/** URL источников таймлайна. Ключи — id версии, ассета или сегмента аниматика. */
export type StudioTimelineSources = {
  versions?: Record<string, string | null>
  assets?: Record<string, string | null>
  animatics?: Record<string, string | null>
}

export type StudioTimelineBundle = {
  timeline: TimelineDocument
  sources: StudioTimelineSources
}

export type StudioShotPlayback = {
  kind: StudioPlaybackKind
  duration_ms: number
  stream_url?: string | null
  poster_url?: string | null
  source_in_ms?: number | null
  source_out_ms?: number | null
  pip?: StudioPipPayload
  /** Документ микромонтажа. Длина ленты считается по нему, элементы — только у окна. */
  timeline?: TimelineDocument | null
  timeline_sources?: StudioTimelineSources | null
}

export type StudioSequenceShot = {
  id: number
  label: string
  posterUrl: string | null
  playback?: StudioShotPlayback | null
  transition?: { leftUrl: string | null; rightUrl: string | null } | null
  /** Документ, который микромонтаж отдал в этой сессии. Важнее playback.timeline. */
  timelineOverride?: StudioTimelineBundle | null
}

export type StudioSequenceClip = {
  shotId: number
  label: string
  startMs: number
  durationMs: number
  kind: StudioPlaybackKind
  streamUrl: string | null
  posterUrl: string | null
  tilePosterUrl: string | null
  transition: { leftUrl: string | null; rightUrl: string | null } | null
  sourceInMs: number | null
  sourceOutMs: number | null
  pip: PipSources
  timeline: TimelineDocument | null
  timelineSources: StudioTimelineSources | null
}

const FALLBACK_DURATION_MS = 4_000

export function buildStudioSequence(shots: readonly StudioSequenceShot[]): StudioSequenceClip[] {
  const clips: StudioSequenceClip[] = []
  let offset = 0
  for (const shot of shots) {
    const playback = shot.playback
    const timeline = resolvedTimeline(shot)
    const timelineMs = timeline ? timelineDuration(timeline) : 0
    const durationMs =
      timelineMs > 0
        ? Math.round(timelineMs)
        : playback && playback.duration_ms > 0
          ? Math.round(playback.duration_ms)
          : FALLBACK_DURATION_MS
    const sourceIn = playback?.source_in_ms
    const sourceOut = playback?.source_out_ms
    const hasWindow = sourceIn != null && sourceOut != null && sourceOut > sourceIn
    clips.push({
      shotId: shot.id,
      label: shot.label,
      startMs: offset,
      durationMs,
      kind: playback?.kind ?? "black",
      streamUrl: playback?.stream_url ?? null,
      posterUrl: playback?.poster_url ?? null,
      tilePosterUrl: shot.posterUrl,
      transition: shot.transition ?? null,
      sourceInMs: hasWindow ? sourceIn : null,
      sourceOutMs: hasWindow ? sourceOut : null,
      pip: pipSourcesFromPlayback(playback?.pip),
      timeline,
      timelineSources: shot.timelineOverride?.sources ?? playback?.timeline_sources ?? null,
    })
    offset += durationMs
  }
  return clips
}

export function studioSequenceDurationMs(clips: readonly StudioSequenceClip[]): number {
  const last = clips[clips.length - 1]
  if (!last) return 0
  return last.startMs + last.durationMs
}

export function resolvedTimeline(shot: StudioSequenceShot): TimelineDocument | null {
  const doc = shot.timelineOverride?.timeline ?? shot.playback?.timeline ?? null
  if (!doc || !Array.isArray(doc.tracks) || doc.tracks.length === 0) return null
  return doc
}

/** Снимок дорожек редактора в ту же форму, что bundle дерева студии. */
export function studioTimelineBundleFromEditor(params: {
  timeline: TimelineDocument
  versionUrls: Record<number, string>
  assetUrls: Record<number, string>
  animaticSegmentId?: number | null
  animaticUrl?: string | null
}): StudioTimelineBundle {
  const versions: Record<string, string> = {}
  for (const [id, url] of Object.entries(params.versionUrls)) {
    if (url) versions[id] = url
  }
  const assets: Record<string, string> = {}
  for (const [id, url] of Object.entries(params.assetUrls)) {
    if (url) assets[id] = url
  }
  const animatics: Record<string, string> = {}
  if (params.animaticSegmentId != null && params.animaticUrl) {
    animatics[String(params.animaticSegmentId)] = params.animaticUrl
  }
  return { timeline: params.timeline, sources: { versions, assets, animatics } }
}

export type StudioPlaybackDocument = {
  doc: TimelineDocument
  sources: EngineSources
}

/**
 * Лента эпизода. Дорожки таймлайна попадают в документ только у expandedShotIds
 * (текущий шот и следующий): у остальных — клип без URL, медиаэлемент не создаётся.
 */
export function buildStudioPlaybackDocument(
  clips: readonly StudioSequenceClip[],
  expandedShotIds: readonly number[],
): StudioPlaybackDocument {
  const expanded = new Set(expandedShotIds)
  const versionUrls: Record<number, string> = {}
  const assetUrls: Record<number, string> = {}
  const animaticUrls: Record<number, string> = {}
  const bedClips: TimelineDocument["tracks"][number]["clips"] = []
  const tracks: TimelineTrack[] = []

  for (const clip of clips) {
    const open = expanded.has(clip.shotId)
    if (open && clip.timeline) {
      copySourceMap(clip.timelineSources?.versions, versionUrls)
      copySourceMap(clip.timelineSources?.assets, assetUrls)
      copySourceMap(clip.timelineSources?.animatics, animaticUrls)
      for (const track of clip.timeline.tracks) {
        tracks.push(offsetTrack(clip.shotId, track, clip.startMs))
      }
      continue
    }

    const playable = open && Boolean(clip.streamUrl)
    if (playable && clip.streamUrl) versionUrls[-clip.shotId] = clip.streamUrl
    bedClips.push(bedClip(clip, playable))
  }

  if (bedClips.length > 0) {
    tracks.unshift({
      id: "studio-bed",
      kind: "video",
      muted: false,
      audio_detached: false,
      hidden: false,
      clips: bedClips,
    })
  }

  return {
    doc: { schema_version: SCHEMA_VERSION, tracks },
    sources: { versionUrls, assetUrls, animaticUrls },
  }
}

function bedClip(clip: StudioSequenceClip, playable: boolean) {
  const length = Math.max(clip.durationMs, 1)
  if (!playable) {
    return {
      id: `${clip.shotId}:hold`,
      source_in_ms: 0,
      source_out_ms: length,
      timeline_start_ms: clip.startMs,
      shot_media_version_id: -clip.shotId,
    }
  }
  const inMs = clip.sourceInMs ?? 0
  const outMs = clip.sourceOutMs != null && clip.sourceOutMs > inMs ? clip.sourceOutMs : inMs + length
  return {
    id: `${clip.shotId}:bed`,
    source_in_ms: inMs,
    source_out_ms: outMs,
    timeline_start_ms: clip.startMs,
    shot_media_version_id: -clip.shotId,
  }
}

function offsetTrack(shotId: number, track: TimelineTrack, offsetMs: number): TimelineTrack {
  return {
    ...track,
    id: `${shotId}:${track.id}`,
    detached_from_track_id: track.detached_from_track_id ? `${shotId}:${track.detached_from_track_id}` : undefined,
    clips: track.clips.map((clip) => ({
      ...clip,
      id: `${shotId}:${clip.id}`,
      timeline_start_ms: clip.timeline_start_ms + offsetMs,
    })),
  }
}

function copySourceMap(raw: Record<string, string | null> | undefined, target: Record<number, string>) {
  if (!raw) return
  for (const [key, url] of Object.entries(raw)) {
    const id = Number(key)
    if (!Number.isFinite(id) || !url) continue
    target[id] = url
  }
}

export function clipAtMs(clips: readonly StudioSequenceClip[], ms: number): StudioSequenceClip | null {
  if (clips.length === 0) return null
  const t = Math.max(0, ms)
  for (const clip of clips) {
    if (t < clip.startMs + clip.durationMs) return clip
  }
  return clips[clips.length - 1] ?? null
}
